// Core
import React from 'react';

// Libraries
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Hooks
import { useBreakpoint } from '../../../../../../hooks/useBreakpoint';

// Types
import { Theme } from '../../../../../../types/theme';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  feederAutomationModeToTagType,
  feederAutomationModeToText,
  isEquipmentRunning,
} from '../../../../../../plugins/crusher';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import { ModuleHeader } from '../../../../../../layout/elements/ModuleHeader';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';
import { Row } from '../../../../../../layout/elements/Row';
import { Tag } from '../../../../../../layout/elements/Tag';
import { Thermometer } from '../../../../../../layout/elements/Thermometer';

// View
import { BinFeederProps } from './BinFeeder.types';
import {
  AutoModeContainer,
  BinFeederContainer,
  ThermometerContainer,
} from './BinFeeder.style';

function BinFeeder({
  className,
  unitData,
  edgeStatus,
  onOpenDrawer,
  testId,
}: BinFeederProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();
  const { isXs } = useBreakpoint();

  return (
    <BinFeederContainer
      data-testid={testId}
      className={className}
    >
      <Row>
        <Col>
          <ModuleHeader
            onClick={onOpenDrawer}
            title={t('view.crusher.vsi.binFeeder.title')}
            testId={`${testId}-module-header`}
          />
        </Col>
      </Row>
      <AutoModeContainer>
        <Paragraph
          size={isXs ? ParagraphSize.xs : ParagraphSize.sm}
          weight={ParagraphWeight.medium}
          color={theme.color.crusherDashboard.statusTitleColor}
          preventOverflow
        >
          {t('view.crusher.vsi.binFeeder.autoMode')}
        </Paragraph>
        <Tag
          type={feederAutomationModeToTagType(
            unitData?.binFeederAutoMode,
            edgeStatus ? edgeStatus?.edgeStatus : null,
            unitData?.timestamp,
          )}
          text={feederAutomationModeToText(unitData?.binFeederAutoMode)}
        />
      </AutoModeContainer>
      <ThermometerContainer>
        <Thermometer
          unit="%"
          running={
            isEquipmentRunning(
              edgeStatus,
              unitData?.timestamp,
              DEVICE_COMMUNICATION_THRESHOLD,
            )
          }
          title={`${t('view.crusher.vsi.binFeeder.speed')}`}
          minValue={0}
          maxValue={100}
          value={unitData?.binFeederSpeedVFD}
        />
      </ThermometerContainer>
    </BinFeederContainer>
  );
}

export { BinFeeder };
